import EditableText from "../Inputs/EditableText";
import {
    CommandBar,
    CommandButton,
    ContextualMenuItemType,
    ICommandBarItemProps,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
    IDropdownOption,
    Image,
    Stack,
    TextField
} from "@fluentui/react";
import NumberField from "../Inputs/NumberField";
import {Dropdown} from "@fluentui/react/lib/Dropdown";
import RichTextField from "../Common/RichTextField";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {IStorefrontSection, IStorefrontSectionCell} from "../../models/Storefront";
import ToggleField from "../Common/ToggleField";
import {
    useGetAllProductSegmentsQuery,
    useLazyGetProductCategoriesBySegmentQuery
} from "../../store/endpoints/ProductCategorizationEndpoints";
import ImageEditor from "../Inputs/ImageEditor";
import {useGetSavedSearchesQuery} from "../../store/endpoints/SavedSearchEndpoints";
import { useGetGlobalStorefrontSettingsQuery, useGetStorefrontQuery } from "../../store/endpoints/StorefrontSettingsEndpoints";
import MarkdownView from "react-showdown";
import StorefrontSectionStyleEditorModal from "./StorefrontSectionStyleEditorModal";
import StorefrontSectionFontEditorModal from "./StorefrontSectionFontEditorModal";
import StorefrontSectionBoxShadowEditorModal from "./StorefrontSectionBoxShadowEditorModal";
import StorefrontSectionLayoutEditorModal from "./StorefrontSectionLayoutEditorModal";
import StorefrontSectionImagePropertyModal from "./StorefrontSectionImagePropertyModal";
import StorefrontSectionAdvancedStyleEditorModal from "./StorefrontSectionAdvancedStyleEditorModal";
import {Css} from "nano-css/addon/vcssom/cssToTree";
import StorefrontSectionSaveTemplateModal from "./StorefrontSectionSaveTemplateModal";
import {useUrlPanelState} from "../../hooks/useUrlState";

interface IStorefrontSectionProps {
    isGlobal?: boolean;
    storefront?: string;
    section: IStorefrontSection;
    saving?: boolean;
    currentIndex: number;
    onChange: (section: IStorefrontSection, immediate?: boolean) => void;
    onMoveUp?: (index: number) => void;
    onMoveDown?: (index: number) => void;
    onDelete: (index: number) => void;
    canMoveDown: boolean;
    onEdit?: () => void;
    onEditComplete?: () => void;
}

const StorefrontSection: React.FC<IStorefrontSectionProps> = (props) => {

    const segments = useGetAllProductSegmentsQuery()
    const savedSearches = useGetSavedSearchesQuery({searchTerm: undefined}) // Using same endpoint for saved searches for now
    const [selectedCategory, setSelectedCategory] = React.useState<string>();
    const [selectedSegment, setSelectedSegment] = React.useState<string>();
    const [categories] = useLazyGetProductCategoriesBySegmentQuery()

    const sfSettings = useGetStorefrontQuery(props.storefront ?? '', {skip: !props.storefront || props.storefront === '' || props.storefront === 'global'});
    const globalSettings = useGetGlobalStorefrontSettingsQuery()
    const [categoryOptions, setCategoryOptions] = React.useState<IDropdownOption[]>([]);
    const [maxWidth, setMaxWidth] = React.useState(800);
    const [showStyleModal, setShowStyleModal] = React.useState(false);
    const [modalContext, setModalContext] = React.useState<string>();
    const [modalIndex, setModalIndex] = React.useState<number | undefined>();
    const [showFontModal, setShowFontModal] = React.useState(false);
    const [selectedSavedSearch, setSelectedSavedSearch] = React.useState<string>();
    const [startWidths, setStartWidths] = React.useState<number[]>([]);
    const [startX, setStartX] = React.useState(0);
    const [resizingFooterIdx, setResizingFooterIdx] = React.useState(0);
    const [resizingHeaderIdx, setResizingHeaderIdx] = React.useState(0);
    const [showBoxShadowModal, setShowBoxShadowModal] = React.useState(false);
    const [showLayoutModal, setShowLayoutModal] = React.useState(false);
    const [showImagePropertyModal, setShowImagePropertyModal] = React.useState(false);
    const [showAdvancedEditorModal, setShowAdvancedEditorModal] = React.useState(false);
    // A link can open one section's style editor: ?panel=sectionStyle&instance=<sectionId>. The
    // storefront travels in the path on this route, so the query's instance is free for the section.
    // These only ADD a way to open a modal - the buttons and their local state are unchanged.
    const styleModalLink = useUrlPanelState('sectionStyle');
    const fontModalLink = useUrlPanelState('sectionFont');
    const boxShadowModalLink = useUrlPanelState('sectionBoxShadow');
    const layoutModalLink = useUrlPanelState('sectionLayout');
    const advancedModalLink = useUrlPanelState('sectionAdvancedStyle');
    const openedByLink = (panel: { isOpen: boolean; instanceId?: string }) =>
        panel.isOpen && !!props.section.id && panel.instanceId === props.section.id;
    const [anyHeaderCellWidthDefined, setAnyHeaderCellWidthDefined] = React.useState(false);
    const [anyFooterCellWidthDefined, setAnyFooterCellWidthDefined] = React.useState(false);
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const [touchscreenDetected, setTouchScreenDetected] = React.useState(false);
    const [showSaveTemplateModal, setShowSaveTemplateModal] = React.useState(false);
    const [isTemplate, setIsTemplate] = React.useState(false);
    // Add refs for anchor elements
    const bodyAnchorRef = React.useRef<HTMLDivElement>(null);
    const titleAnchorRef = React.useRef<HTMLDivElement>(null);
    const headerCellAnchorRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const footerCellAnchorRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    const headerRowRef = React.useRef<HTMLDivElement>(null);
    const footerRowRef = React.useRef<HTMLDivElement>(null);

    const isTouchDevice = (): boolean => {
        return (
            ('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0)
        );
    }

    useEffect(() => {
        console.log('storefront:', props.storefront);
        if(props.storefront && props.storefront === 'template'){
            setIsTemplate(true);
        }
        else{
            setIsTemplate(false);
        }
    }, [props.storefront]);

    useEffect(() => {
        if(isTouchDevice()){
            setTouchScreenDetected(true);
        }
    }, []);

    useEffect(() => {
        if(props.storefront === 'global' && globalSettings.data?.maxWidth){
            setMaxWidth(globalSettings.data.maxWidth);
        }
        else if(props.storefront && props.storefront !== 'global' && sfSettings.data?.maxWidth){
            setMaxWidth(sfSettings.data.maxWidth);
        }
        else{
            setMaxWidth(800);
        }

    }, [sfSettings.data, globalSettings.data])
    useEffect(() => {
        if(props.section.categories && props.section.categories.length === 1){
            setSelectedCategory(props.section.categories[0]);
        } else {
            setSelectedCategory(undefined);
        }
        if(props.section.segments && props.section.segments.length === 1){
            setSelectedSegment(props.section.segments[0]);
        } else {
            setSelectedSegment(undefined);
            setSelectedCategory(undefined);
        }
    }, [props.section.categories, props.section.segments]);


    useEffect(() => {
        if(props.section.headerCells) {
            const anyWidthDefined = props.section.headerCells.some(cell => {
                const s = cell.styleProps || {};
                return s.width || s.minWidth || s.maxWidth;
            });
            setAnyHeaderCellWidthDefined(anyWidthDefined);
        }
    }, [props.section.headerCells]);

    useEffect(() => {
        if(props.section.footerCells) {
            const anyWidthDefined = props.section.footerCells.some(cell => {
                const s = cell.styleProps || {};
                return s.width || s.minWidth || s.maxWidth;
            });
            setAnyFooterCellWidthDefined(anyWidthDefined);
        }
    }, [props.section.footerCells]);

    useEffect(() => {
        setSelectedSavedSearch(props.section.savedSearchId);
    }, [props.section.savedSearchId]);


    const [resettingSegment, setResettingSegment] = React.useState(false);

    useEffect(() => {
        if (resettingSegment) {
            setSelectedCategory(undefined);
            setResettingSegment(false);
            return;
        }
        if (props.section.categories && props.section.categories.length === 1) {
            setSelectedCategory(props.section.categories[0]);
        } else {
            setSelectedCategory(undefined);
        }
        if (props.section.segments && props.section.segments.length === 1) {
            setSelectedSegment(props.section.segments[0]);
        } else {
            setSelectedSegment(undefined);
            setSelectedCategory(undefined);
        }
    }, [props.section.categories, props.section.segments, resettingSegment]);

    React.useEffect(() => {
        const fetchCategories = async () => {
            if(selectedSegment){
                const cats = await categories(selectedSegment).unwrap();
                if(cats?.items){
                    setCategoryOptions([{key: '', text: ''}, ...cats.items.map(c => ({key: c.identifier ?? c.id ?? '', text: c.label ?? c.identifier ?? c.id ?? ''}))]);
                }
            } else {
                setCategoryOptions([]);
            }
        }
        fetchCategories();
    }, [selectedSegment]);

    const dropdownOptions: IDropdownOption[] = [
        { key: 'featured', text: 'Featured Products' },
        { key: 'bundles', text: 'Bundles' },
        { key: 'dynamicProductListing', text: 'Dynamic Product Listings' },
        { key: 'savedSearches', text: 'Saved Searches' },
        { key: 'selectedProducts', text: 'Selected Products' },
        { key: 'categoriesSegments', text: 'Categories/Segments' },
        { key: 'promos', text: 'On Promotion' },
    ]

    const getOverflowCommands = (): ICommandBarItemProps[] => {
        return[
            {
                key: 'saveTemplate',
                text: (isTemplate ? 'Copy as new Template' : 'Save as Template'),
                title: (isTemplate ? 'Copy as new Template' : 'Save as Template'),
                iconProps: {iconName: 'SaveTemplate'},
                disabled: (props.saving),
                onClick: () => {
                    setShowSaveTemplateModal(true)
                }
            },]
    }

    const getFarSectionCommands = (): ICommandBarItemProps[] => {
        return [
            {
                key: 'editSave',
                text: (editMode ? 'Save' : 'Edit'),
                disabled: props.saving,
                iconProps: {iconName: (editMode ? 'Save' : 'Edit')},
                onClick: () => {
                    if(editMode){
                        setEditMode(false)
                    }
                    else{
                        setEditMode(true)
                        setPreviewMode(false)
                    }
                }
            },
            {
                key: 'preview',
                text: (previewMode ? 'Hide Preview': 'Preview'),
                disabled: props.saving || editMode,
                iconProps: {iconName: 'View'},
                onClick: () => {previewMode ? setPreviewMode(false) : setPreviewMode(true)}
            }
        ]
    }

    const getSectionCommands = (): ICommandBarItemProps[] => {
        const upDown = props.onMoveDown && props.onMoveDown ?  [
            {
                key: 'moveUp',
                text: 'Move Up',
                title: 'Move Up',
                iconProps: {iconName: 'ChevronUp'},
                disabled: props.currentIndex === 0 || props.saving,
                onClick: () => {
                    props.onMoveUp?.(props.currentIndex);

                }
            },
            {
                key: 'moveDown',
                text: 'Move Down',
                title: 'Move Down',
                iconProps: {iconName: 'ChevronDown'},
                disabled: (!props.canMoveDown || props.saving),
                onClick: () => {
                    props.onMoveDown?.(props.currentIndex);
                }
            },
        ] : []

        const enableDisable = isTemplate ?  [] :
            [
                {
                    key: 'enableDisable',
                    text: 'Published',
                    title: 'Publish Section',
                    disabled: props.saving,
                    onRender: () => <ToggleField inline={true} checked={props.section.disabled != true} label={'Published'} onChange={(e, v) => {
                        props.onChange({...props.section, disabled: v === false})
                    }} />,
                }]

        return [
            ...enableDisable,
            ...upDown,
            {
                key: 'delete',
                text: 'Delete',
                title: 'Delete Section',
                disabled: props.saving,
                iconProps: {iconName: 'Delete'},
                onClick: () => {
                    props.onDelete(props.currentIndex);
                }
            }
        ]
    }

    const getSegmentOptions = (): IDropdownOption[] => {
        if(segments.data?.items){
            return [...segments.data.items.map(s => ({key: s.identifier ?? s.id ?? '', text: s.label ?? s.identifier ?? s.id ?? ''}))];
        }
        return [];
    }

    const getSavedSearchOptions = (): IDropdownOption[] => {
        if(savedSearches.data && props.storefront){
            const filtered = props.isGlobal ? savedSearches.data.filter(ss => ss.dynamicProductListing === true && (ss.includeInAllStorefronts == true))
                : savedSearches.data.filter(ss => ss.dynamicProductListing === true && (ss.storefronts?.includes(props.storefront ?? '') || ss.includeInAllStorefronts == true));
            return [...filtered.map(s => ({key: s.id ?? '', text: s.name ?? s.id ?? ''}))];
        }
        return [];
    }

    const handleHeaderCellValueChange = (cell: IStorefrontSectionCell, index: number) => {
        const updated = [...props.section.headerCells ?? []];
        updated.splice(index, 1, cell);
        props.onChange({ ...props.section, headerCells: updated });
    }
    const handleFooterCellValueChange = (cell: IStorefrontSectionCell, index: number) => {
        const updated = [...props.section.footerCells ?? []];
        updated.splice(index, 1, cell);
        props.onChange({ ...props.section, footerCells: updated });
    }

    // Add/remove header cell
    const addHeaderCell = (type: "text" | "video" | "image") => {
        const newCell = { type: type, title: "New Cell", minWidth: 100, styleProps: {} };
        props.onChange({ ...props.section, headerCells: [...props.section.headerCells ?? [], newCell] });
    };
    const removeHeaderCell = (idx: number) => {
        const updatedCells = (props.section.headerCells || []).filter((_, i) => i !== idx);
        props.onChange({ ...props.section, headerCells: updatedCells }, true);
    };

    // Reorder header cells (move left/right)
    const moveHeaderCellLeft = (idx: number) => {
        if (idx <= 0) return; // Can't move leftmost cell further left
        const cells = [...(props.section.headerCells || [])];
        const [movedCell] = cells.splice(idx, 1);
        cells.splice(idx - 1, 0, movedCell);
        props.onChange({ ...props.section, headerCells: cells }, true);
    };

    const moveHeaderCellRight = (idx: number) => {
        if (!props.section.headerCells || idx >= props.section.headerCells.length - 1) return; // Can't move rightmost cell further right
        const cells = [...(props.section.headerCells || [])];
        const [movedCell] = cells.splice(idx, 1);
        cells.splice(idx + 1, 0, movedCell);
        props.onChange({ ...props.section, headerCells: cells }, true);
    };

    const addFooterCell = (type: "text" | "video" | "image") => {
        const newCell = { type: type, title: "New Cell", minWidth: 100, styleProps: {} };
        props.onChange({ ...props.section, footerCells: [...props.section.footerCells ?? [], newCell] });
    };
    const removeFooterCell = (idx: number) => {
        const updatedCells = (props.section.footerCells || []).filter((_, i) => i !== idx);
        props.onChange({ ...props.section, footerCells: updatedCells }, true);
    };

    const moveFooterCellLeft = (idx: number) => {
        if (idx <= 0) return; // Can't move leftmost cell further left
        const cells = [...(props.section.footerCells || [])];
        const [movedCell] = cells.splice(idx, 1);
        cells.splice(idx - 1, 0, movedCell);
        props.onChange({ ...props.section, footerCells: cells }, true);
    };

    const moveFooterCellRight = (idx: number) => {
        if (!props.section.footerCells || idx >= props.section.footerCells.length - 1) return;
        const cells = [...(props.section.footerCells || [])];
        const [movedCell] = cells.splice(idx, 1);
        cells.splice(idx + 1, 0, movedCell);
        props.onChange({ ...props.section, footerCells: cells }, true);
    };

    const handleHeaderGrabberMouseDown = (idx: number, e: React.MouseEvent<HTMLDivElement>) => {
        setResizingFooterIdx(idx);
        setStartX(e.clientX);
        setStartWidths((props.section.headerCells || []).map(cell => cell.styleProps?.width || ""));
        document.body.style.cursor = "col-resize";
    };

    const handleFooterGrabberMouseDown = (idx: number, e: React.MouseEvent<HTMLDivElement>) => {
        setResizingHeaderIdx(idx);
        setStartX(e.clientX);
        setStartWidths((props.section.footerCells || []).map(cell => cell.styleProps?.width || ""));
        document.body.style.cursor = "col-resize";
    };

    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const [isTitleHovered, setIsTitleHovered] = useState(false);
    const [isBodyHovered, setIsBodyHovered] = useState(false);
    const [isFooterPodHovered, setIsFooterPodHovered] = useState(false);
    const [isHeaderPodHovered, setIsHeaderPodHovered] = useState(false);
    const [hoveredFooterPodIndex, setHoveredFooterPodIndex] = useState<number>(0);
    const [hoveredHeaderPodIndex, setHoveredHeaderPodIndex] = useState<number>(0);
    const [isHeaderRowHovered, setIsHeaderRowHovered] = useState(false);
    const [isFooterRowHovered, setIsFooterRowHovered] = useState(false);
    const [isProductRowHovered, setIsProductRowHovered] = useState(false);

    useEffect(() => {
        if(editMode){
            props.onEdit?.();
        }
        else{
            props.onEditComplete?.();
        }
    }, [editMode]);

    const getMockItems = () => {
        const list = [];
        for (let i = 1; i <= (props.section?.maxEntries ?? 6); i++) {
            list.push(i);
        }

        return (
            <div className={'section-featured-product-display'} style={{...props.section.productStyleProps}}>
                <div className={'card-container'}>
                    {list.map(i => (
                        <div className={'card'} key={i} style={{ width: 250, height: 250, background: '#eee', marginRight: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            Item {i}
                        </div>))}
                </div>

            </div>
        )
    }

    function cleanCssObject(css: Css): Css {
        return Object.fromEntries(
            Object.entries(css).filter(
                ([, value]) =>
                    value !== undefined &&
                    value !== null &&
                    !(typeof value === 'string' && value.trim() === '') &&
                    !(Array.isArray(value) && value.length === 0) &&
                    !(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
            )
        );
    }

    function usePutStyleMenuModalProps(context: string, section: IStorefrontSection, onChange: (section: IStorefrontSection) => void, index?: number) {
        return useMemo(() => {
            if(context == 'HeaderCell' && index != undefined && section?.headerCells?.[index]){
                const onChange = (css: Css) => {
                    const updatedCells = section.headerCells?.map((cell, idx) =>
                        idx === index ? {...cell, styleProps: {...cleanCssObject(css)}} : cell
                    ) || [];
                    props.onChange({...section, headerCells: updatedCells});
                }

                return {onChange: onChange, css: section.headerCells[index]?.styleProps ?? {} };

            }
            else if(context == 'FooterCell' && index != undefined && section?.footerCells?.[index]){
                const onChange = (css: Css) => {
                    const updatedCells = section.footerCells?.map((cell, idx) =>
                        idx === index ? {...cell, styleProps: {...cleanCssObject(css)}} : cell
                    ) || [];
                    props.onChange({...section, footerCells: updatedCells});
                }
                return {onChange: onChange, css: section.footerCells[index]?.styleProps ?? {} };

            }
            else if(context == 'Footer'){
                const onChange = (css: Css)=> {
                    props.onChange({...section, footerStyleProps: {...cleanCssObject(css)}})
                }
                return {onChange: onChange, css: section.footerStyleProps ?? {}};
            }
            else if(context == 'Header'){
                const onChange = (css: Css)=> {
                    props.onChange({...section, headerStyleProps: {...cleanCssObject(css)}})
                }
                return {onChange: onChange, css: section.headerStyleProps ?? {}};
            }
            else if(context == 'Title'){
                const onChange = (css: Css)=> {
                    props.onChange({...props.section, titleStyleProps: {...cleanCssObject(css)}})
                }
                return {onChange: onChange, css: section.titleStyleProps ?? {}};
            }
            else if(context == 'Product'){
                const onChange = (css: Css)=> {
                    props.onChange({...section, productStyleProps: {...cleanCssObject(css)}})
                }
                return {onChange: onChange, css: section.productStyleProps ?? {}};
            }
            else if(context == 'Body'){
                const onChange = (css: Css)=> {
                    props.onChange({...section, styleProps: {...cleanCssObject(css)}})
                }
                return {onChange: onChange, css: section.styleProps ?? {}};
            }
            return undefined;
        }, [context, index, section, onChange]);
    }

    function usePatchStyleMenuModalProps(context: string, section: IStorefrontSection, onChange: (section: IStorefrontSection) => void, index?: number) {
        return useMemo(() => {
            if(context == 'HeaderCell' && index != undefined && section?.headerCells?.[index]){
                const onChange = (key: string, value: any) => {
                    const updatedCells = section.headerCells?.map((cell, idx) =>
                        idx === index ? {...cell, styleProps: {...cell.styleProps, [key]: value}} : cell
                    ) || [];
                    props.onChange({...section, headerCells: updatedCells});
                }

                return {onChange: onChange, css: section.headerCells[index]?.styleProps ?? {} };

            }
            else if(context == 'FooterCell' && index != undefined && section?.footerCells?.[index]){
                const onChange = (key: string, value: any) => {
                    const updatedCells = section.footerCells?.map((cell, idx) =>
                        idx === index ? {...cell, styleProps: {...cell.styleProps, [key]: value}} : cell
                    ) || [];
                    props.onChange({...section, footerCells: updatedCells});
                }
                return {onChange: onChange, css: section.footerCells[index]?.styleProps ?? {} };

            }
            else if(context == 'Footer'){
                const onChange = (key: string, value: any)=> {
                    props.onChange({...section, footerStyleProps: {...section.footerStyleProps, [key]: value}})
                }
                return {onChange: onChange, css: section.footerStyleProps ?? {}};
            }
            else if(context == 'Header'){
                const onChange = (key: string, value: any)=> {
                    props.onChange({...section, headerStyleProps: {...section.headerStyleProps, [key]: value}})
                }
                return {onChange: onChange, css: section.headerStyleProps ?? {}};
            }
            else if(context == 'Title'){
                const onChange = (key: string, value: any)=> {
                    props.onChange({...props.section, titleStyleProps: {...section.titleStyleProps, [key]: value}})
                }
                return {onChange: onChange, css: section.titleStyleProps ?? {}};
            }
            else if(context == 'Product'){
                const onChange = (key: string, value: any)=> {
                    props.onChange({...section, productStyleProps: {...section.productStyleProps, [key]: value}})
                }
                return {onChange: onChange, css: section.productStyleProps ?? {}};
            }
            else if(context == 'Body'){
                const onChange = (key: string, value: any)=> {
                    props.onChange({...section, styleProps: {...section.styleProps, [key]: value}})
                }
                return {onChange: onChange, css: section.styleProps ?? {}};
            }
            return undefined;
        }, [context, index, section, onChange]);
    }

    function usePatchStyleMenuModalImageProps(context: string, section: IStorefrontSection, onChange: (section: IStorefrontSection) => void, index?: number) {
        return useMemo(() => {
            if(context == 'HeaderCell' && index != undefined && section?.headerCells?.[index]){
                const onChangeLinkUrl = (value: string) => {
                    const updatedCells = section.headerCells?.map((cell, idx) =>
                        idx === index ? {...cell, linkUrl: value} : cell
                    ) || [];
                    props.onChange({...section, headerCells: updatedCells});
                }
                const onChangeAltText = (value: string) => {
                    const updatedCells = section.headerCells?.map((cell, idx) =>
                        idx === index ? {...cell, altText: value} : cell
                    ) || [];
                    props.onChange({...section, headerCells: updatedCells});
                }

                return {onChangeLinkUrl: onChangeLinkUrl, onChangeAltText: onChangeAltText, cell: section.headerCells[index]};

            }
            else if(context == 'FooterCell' && index != undefined && section?.footerCells?.[index]){
                const onChangeLinkUrl = (value: string) => {
                    const updatedCells = section.footerCells?.map((cell, idx) =>
                        idx === index ? {...cell, linkUrl: value} : cell
                    ) || [];
                    props.onChange({...section, footerCells: updatedCells});
                }
                const onChangeAltText = (value: string) => {
                    const updatedCells = section.footerCells?.map((cell, idx) =>
                        idx === index ? {...cell, altText: value} : cell
                    ) || [];
                    props.onChange({...section, footerCells: updatedCells});
                }
                return {onChangeLinkUrl: onChangeLinkUrl, onChangeAltText: onChangeAltText, cell: section.footerCells[index]};
            }
            return undefined;
        }, [context, index, section, onChange]);
    }

    const patchStyleMenuModalProps = usePatchStyleMenuModalProps(modalContext ?? '', props.section, props.onChange, modalIndex);
    const patchStyleMenuModalImageProps = usePatchStyleMenuModalImageProps(modalContext ?? '', props.section, props.onChange, modalIndex);
    const putStyleMenuModalProps = usePutStyleMenuModalProps(modalContext ?? '', props.section, props.onChange, modalIndex);


    const getMenuItems = (context: 'Body' | 'Product' | 'Title' | 'Header' | 'Footer' | 'FooterCell' | 'HeaderCell', index?: number, isImage?: boolean): IContextualMenuProps => {

        const baseItems = [
            {
                key: 'style',
                iconProps: {iconName: 'BorderDash'},
                text: `Edit ${context} Border/Background`,
                title: 'Edit Style',
                disabled: props.saving,
                onClick: () => {
                    setModalContext(context);
                    setModalIndex(index);
                    setShowStyleModal(true)
                }
            },
            {
                key: 'font',
                iconProps: {iconName: 'Font'},
                text: `Edit ${context} Font`,
                title: 'Edit Font',
                disabled: props.saving,
                onClick: () => {
                    setModalContext(context);
                    setModalIndex(index);
                    setShowFontModal(true)
                }
            },
            {
                key: 'boxShadow',
                iconProps: {iconName: 'LightningBolt'},
                text: `Edit ${context} Effects`,
                title: 'Edit Effects',
                disabled: props.saving,
                onClick: () => {
                    setModalContext(context);
                    setModalIndex(index);
                    setShowBoxShadowModal(true)
                }
            },
            {
                key: 'layout',
                iconProps: {iconName: 'LargeGrid'},
                text: `Edit ${context} Layout`,
                title: 'Edit Layout',
                disabled: props.saving,
                onClick: () => {
                    setModalContext(context);
                    setModalIndex(index);
                    setShowLayoutModal(true)
                }
            },
            {
                key: 'advanced',
                iconProps: {iconName: 'RawSource'},
                text: `Advanced Editor`,
                title: 'Advanced Editor',
                disabled: props.saving,
                onClick: () => {
                    setModalContext(context);
                    setModalIndex(index);
                    setShowAdvancedEditorModal(true)
                }
            }
        ]

        if(isImage){
            const specialImageHeaderItems : IContextualMenuItem[] = [
                { key: 'divider', itemType: ContextualMenuItemType.Divider},
                { key: 'addNew', itemType: ContextualMenuItemType.Header, text: 'Add New' },
                { key: 'imageProperties', text: 'Edit Image Properties', iconProps: {iconName: 'PictureCenter'}, onClick: () => {
                        setModalContext(context);
                        setModalIndex(index);
                        setShowImagePropertyModal(true)}
                },
            ]
            return {
                items: [...baseItems, ...specialImageHeaderItems ]
            }
        }

        if(context === 'Header'){
            const specialHeaderMenuItems : IContextualMenuItem[] = [
                { key: 'divider', itemType: ContextualMenuItemType.Divider},
                { key: 'addNew', itemType: ContextualMenuItemType.Header, text: 'Add New' },
                { key: 'newImage', text: 'Image', iconProps: {iconName: 'PictureCenter'}, onClick: () => addHeaderCell('image')},
                { key: 'newText', text: 'Text', iconProps: {iconName: 'InsertTextBox'}, onClick: () => addHeaderCell('text') },
                { key: 'newVideo', text: 'Video', iconProps: {iconName: 'Video'}, onClick: () => addHeaderCell('video') },
            ]
            return {
                items: [...baseItems, ...specialHeaderMenuItems ]
            }
        }
        if(context === 'Footer'){
            const specialHeaderMenuItems : IContextualMenuItem[] = [
                { key: 'divider', itemType: ContextualMenuItemType.Divider},
                { key: 'addNew', itemType: ContextualMenuItemType.Header, text: 'Add New' },
                { key: 'newImage', text: 'Image', iconProps: {iconName: 'PictureCenter'}, onClick: () => addFooterCell('image')},
                { key: 'newText', text: 'Text', iconProps: {iconName: 'InsertTextBox'}, onClick: () => addFooterCell('text') },
                { key: 'newVideo', text: 'Video', iconProps: {iconName: 'Video'}, onClick: () => addFooterCell('video') },
            ]
            return {
                items: [...baseItems, ...specialHeaderMenuItems ]
            }
        }
        return {
            items: baseItems
        }
    }


    // Utility to extract YouTube video ID from various URL formats
    function extractYouTubeVideoId(url: string): string | null {
        if (!url) return null;
        // Match standard watch URLs
        const watchMatch = url.match(/[?&]v=([\w-]{11})/);
        if (watchMatch) return watchMatch[1];
        // Match embed URLs
        const embedMatch = url.match(/\/embed\/([\w-]{11})/);
        if (embedMatch) return embedMatch[1];
        // Match short URLs
        const shortMatch = url.match(/youtu\.be\/([\w-]{11})/);
        if (shortMatch) return shortMatch[1];
        // Match direct youtube.com/VIDEO_ID
        const directMatch = url.match(/youtube\.com\/([\w-]{11})/);
        if (directMatch) return directMatch[1];
        return null;
    }

    const getCellButtons = (idx: number, count: number, footer?: boolean, isImage?: boolean) => {
        return (
            <div>
                <div style={{ position: 'absolute', top: 5, right: 5, zIndex: 20, display: 'flex', gap: 4, background: 'rgba(255,255,255,0.8)', padding: 2, borderRadius: 4 }}>
                    <CommandButton
                        iconProps={{ iconName: 'EditStyle' }}
                        menuProps={getMenuItems(footer ? 'FooterCell' : 'HeaderCell', idx, isImage)}
                    />
                </div>
                <div style={{ position: 'absolute', bottom: 5, right: 5, zIndex: 20, display: 'flex', gap: 4, background: 'rgba(255,255,255,0.8)', padding: 2, borderRadius: 4 }}>
                    {(idx > 0 && count > 1) && (
                        <IconButton
                            iconProps={{ iconName: 'ChevronLeft' }}
                            title="Move Left"
                            ariaLabel="Move Left"
                            onClick={() => footer ? moveFooterCellLeft(idx) : moveHeaderCellLeft(idx)}
                            styles={{ root: { height: 24, width: 24, minWidth: 24 } }}
                        />
                    )}
                    {(idx < (count-1)) && (
                        <IconButton
                            iconProps={{ iconName: 'ChevronRight' }}
                            title="Move Right"
                            ariaLabel="Move Right"
                            onClick={() => footer ? moveFooterCellRight(idx) : moveHeaderCellRight(idx)}
                            styles={{ root: { height: 24, width: 24, minWidth: 24 } }}
                        />
                    )}
                    <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title="Remove Pod"
                        ariaLabel="Remove Pod"
                        onClick={() => footer ? removeFooterCell(idx) : removeHeaderCell(idx)}
                        styles={{ root: { height: 24, width: 24, minWidth: 24 } }}
                    />
                </div>
            </div>)
    }

    return (
        <div
            style={{ width: '100%'}}
        >
            <StorefrontSectionStyleEditorModal {...patchStyleMenuModalProps} displayModal={showStyleModal || openedByLink(styleModalLink)} closer={() => { setShowStyleModal(false); styleModalLink.close(); }}  />
            <StorefrontSectionFontEditorModal {...patchStyleMenuModalProps} displayModal={showFontModal || openedByLink(fontModalLink)} closer={() => { setShowFontModal(false); fontModalLink.close(); }}  />
            <StorefrontSectionBoxShadowEditorModal {...patchStyleMenuModalProps} displayModal={showBoxShadowModal || openedByLink(boxShadowModalLink)} closer={() => { setShowBoxShadowModal(false); boxShadowModalLink.close(); }}  />
            <StorefrontSectionLayoutEditorModal {...patchStyleMenuModalProps} displayModal={showLayoutModal || openedByLink(layoutModalLink)} closer={() => { setShowLayoutModal(false); layoutModalLink.close(); }}  />
            <StorefrontSectionImagePropertyModal {...patchStyleMenuModalImageProps} displayModal={showImagePropertyModal} closer={() => setShowImagePropertyModal(false)}  />
            <StorefrontSectionAdvancedStyleEditorModal {...putStyleMenuModalProps} displayModal={showAdvancedEditorModal || openedByLink(advancedModalLink)} closer={() => { setShowAdvancedEditorModal(false); advancedModalLink.close(); }}  />
            {(props.storefront && props.section.id) && <StorefrontSectionSaveTemplateModal sectionId={props.section.id} title={props.section.title} storefrontId={props.storefront} displayModal={showSaveTemplateModal} closer={() => setShowSaveTemplateModal(false)} />}
            <h3>{props.section.title ?? `Section ${props.section.sortOrder}`}</h3>
            <CommandBar items={getSectionCommands()} farItems={getFarSectionCommands()} overflowItems={getOverflowCommands()}/>
                <Stack horizontal={true} style={{paddingBottom: '1em'}}>
                    <div style={{display: 'inline-flex', gap: '1em', alignItems: 'flex-start', minWidth: '200px'}}>
                        <Dropdown
                            label='Section Type'
                            options={dropdownOptions}
                            selectedKey={props.section.type}
                            onChange={(e, option) => {
                                if (option) {
                                    setSelectedCategory(undefined);
                                    setSelectedSegment(undefined);
                                    setSelectedSavedSearch(undefined);
                                    props.onChange({...props.section, type: option.key as string})
                                }
                            }}
                            placeholder='Select Section Type'
                        />
                    </div>
                    {props.section.type === 'categoriesSegments' &&
                    <React.Fragment>
                        <div style={{display: 'inline-flex', gap: '1em', alignItems: 'flex-start', minWidth: '200px'}}>
                            <Dropdown
                                label='Segment'
                                options={getSegmentOptions()}
                                selectedKey={selectedSegment}
                                // In the segment dropdown onChange:
                                onChange={(e, option) => {
                                    if (option) {
                                        setResettingSegment(true);
                                        setSelectedCategory(undefined);
                                        props.onChange({
                                            ...props.section,
                                            segments: [option.key as string],
                                            categories: []
                                        });
                                    }
                                }}
                                placeholder='Select Segment'
                            />
                        </div>
                        {props.section.segments && props.section.segments.length === 1 &&
                        <div style={{display: 'inline-flex', gap: '1em', alignItems: 'flex-start', minWidth: '200px'}}>
                            <Dropdown
                                label='Category'
                                options={categoryOptions}
                                disabled={!props.section?.segments || props.section.segments.length !== 1}
                                selectedKey={selectedCategory}
                                onChange={(e, option) => {
                                    if (option) {
                                        props.onChange({...props.section, categories: [option.key as string]})
                                    }
                                }}
                                placeholder='Select Category'
                            />
                        </div>}
                    </React.Fragment>}
                    {props.section.type === 'dynamicProductListing' &&
                        <div style={{display: 'inline-flex', gap: '1em', alignItems: 'flex-start', minWidth: '200px'}}>
                            <Dropdown
                                label='Source Saved Search'
                                options={getSavedSearchOptions()}
                                selectedKey={selectedSavedSearch}
                                onChange={(e, option) => {
                                    if (option) {
                                        props.onChange({
                                            ...props.section,
                                            savedSearchId: option.key as string
                                        });
                                    }
                                }}
                                placeholder='Select Saved Search'
                            />
                        </div>
                    }
                    <div style={{display: 'inline-flex', gap: '1em', alignItems: 'flex-start', minWidth: '200px'}}>
                        <NumberField
                            label='Number of Products'
                            value={props.section.maxEntries ?? 4}
                            onChange={(value) => {
                                props.onChange({...props.section, maxEntries: value})
                            }}
                        />
                    </div>
                </Stack>
            <div
                ref={bodyAnchorRef}
                style={{width: '100%'}}
                onMouseEnter={() => setIsBodyHovered(true)}
                onMouseLeave={() => setIsBodyHovered(false)}
            >

                {(editMode || previewMode) ? 
                <div style={{ backgroundColor: '#fff', width: '100%' }}>
                    {(editMode && (isBodyHovered || touchscreenDetected)) && (
                        <div style={{ position: 'relative', top: 5, right: 5, zIndex: 20, display: 'flex', gap: 4, background: 'rgba(255,255,255,0.8)', padding: 2, borderRadius: 4 }}>
                            <CommandButton
                                iconProps={{ iconName: 'EditStyle' }}
                                menuProps={getMenuItems('Body')}
                            />
                        </div>
                    )}
                    <div style={{...props.section.styleProps, maxWidth: maxWidth }}>

                        {/* Title */}
                        <div
                            style={{ ...props.section.titleStyleProps, width: '100%', position: 'relative' }}
                            draggable={!editMode}
                            onMouseLeave={() => {setIsTitleHovered(false)}} onMouseEnter={() => setIsTitleHovered(true)} ref={titleAnchorRef}
                        >
                            {editMode ? (
                                <div>
                                    {(isTitleHovered || touchscreenDetected) && (
                                        <div style={{ position: 'absolute', top: 5, right: 5, zIndex: 20, display: 'flex', gap: 4, background: 'rgba(255,255,255,0.8)', padding: 2, borderRadius: 4 }}>
                                            <CommandButton
                                                iconProps={{ iconName: 'EditStyle' }}
                                                menuProps={getMenuItems('Title')}
                                            />
                                        </div>
                                    )}
                                    <EditableText value={props.section.title || ''} onChange={val => props.onChange({ ...props.section, title: val })} style={{fontSize: props.section.titleStyleProps?.fontSize, fontFamily: props.section.titleStyleProps?.fontFamily, color: props.section.titleStyleProps?.color}} />
                                </div>
                            ) : (<span style={{ fontSize: props.section.titleStyleProps?.fontSize, fontFamily: props.section.titleStyleProps?.fontFamily, color: props.section.titleStyleProps?.color }}>{props.section.title}</span>)}
                        </div>
                        {/* Header row */}
                        <div className={(editMode ? "header-row-container-edit" : "header-row-container")}
                             ref={headerRowRef}
                             onMouseEnter={() => setIsHeaderRowHovered(true)}
                             onMouseLeave={() => setIsHeaderRowHovered(false)}
                        >
                            {editMode && (isHeaderRowHovered || touchscreenDetected || !props.section.headerCells || props.section.headerCells.length === 0) &&
                                <div>
                                    <CommandButton
                                        iconProps={{ iconName: 'EditStyle' }}
                                        menuProps={getMenuItems('Header')}
                                    />
                                </div>}
                            <div style={{...props.section.headerStyleProps, display: 'flex', flexDirection: 'row', width: '100%'}}>
                                {(props.section.headerCells || []).map((cell, idx) => {

                                    const s = cell.styleProps || {};
                                    // If any cell has width defined, only cells without width fill remaining space
                                    const heightProps = {height: s.height, minHeight: s.minHeight, maxHeight: s.maxHeight};
                                    const widthProps = {width: s.width, minWidth: s.minWidth, maxWidth: s.maxWidth}
                                    let baseFlex = {};
                                    if (anyHeaderCellWidthDefined) {
                                        if (!(s.width || s.minWidth || s.maxWidth)) {
                                            baseFlex = { flex: '1 1 0', ...heightProps };
                                        } else {
                                            baseFlex = { flex: '0 0 auto', ...widthProps, ...heightProps };
                                        }
                                    } else {
                                        baseFlex = { flex: '1 1 0', ...heightProps };
                                    }

                                    return(<React.Fragment key={idx}>
                                        <div
                                            onMouseEnter={() => {setIsHeaderPodHovered(true); setHoveredHeaderPodIndex(idx)}} onMouseLeave={() => {setIsHeaderPodHovered(false); setHoveredHeaderPodIndex(idx)}}
                                            className={(editMode ? "section-cell-edit" : "section-cell")}
                                            ref={el => headerCellAnchorRefs.current[idx] = el}
                                            style={{ position: 'relative', boxSizing: 'border-box',...baseFlex}}
                                        >
                                            <React.Fragment>
                                                {/* Render cell content by type */}
                                                {cell.type === 'image' ?
                                                    <React.Fragment>
                                                        {/* Add pod control buttons in edit mode */}
                                                        {(editMode && ((hoveredHeaderPodIndex === idx && isHeaderPodHovered) || touchscreenDetected)) && (
                                                            <div>
                                                                {getCellButtons(idx, props.section?.headerCells?.length ?? 0,false, true)}
                                                            </div>
                                                        )}
                                                        {editMode ?
                                                            <div style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}>
                                                                <ImageEditor value={cell.sourceUrl} onChange={(url) => {if(editMode){handleHeaderCellValueChange({...cell,  sourceUrl: url}, idx)}}} width={'100%'} height={'100%'} />
                                                            </div> :
                                                            <Image src={cell.sourceUrl} style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}></Image>}
                                                    </React.Fragment> : null}
                                                {cell.type === 'text' ?
                                                    <React.Fragment>
                                                        {/* Add pod control buttons in edit mode */}
                                                        {(editMode && ((isHeaderPodHovered && hoveredHeaderPodIndex == idx) || touchscreenDetected)) &&  (
                                                            <div>
                                                                {getCellButtons(idx, props.section?.headerCells?.length ?? 0)}
                                                            </div>
                                                        )}

                                                        {editMode ?
                                                            <div style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}>
                                                                <RichTextField value={cell.text || ''} ref={bodyAnchorRef} previewStyle={{background: 'unset', fontSize: cell.styleProps?.fontSize, fontFamily: cell.styleProps?.fontFamily, fontStyle: cell.styleProps?.fontStyle, color: cell.styleProps?.color, fontWeight: cell.styleProps?.fontWeight}} onChange={(url) => {if(editMode){handleHeaderCellValueChange({...cell,  text: url}, idx)}}} readOnly={!editMode} />
                                                            </div>
                                                            :
                                                            <div style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}>
                                                                <MarkdownView className={'text-wysiwyg'} markdown={cell.text ?? ''} />
                                                            </div>}
                                                    </React.Fragment> : null}
                                                {cell.type === 'video' ?
                                                    <React.Fragment>
                                                        {/* Add pod control buttons in edit mode */}
                                                        {(editMode && ((isHeaderPodHovered && hoveredHeaderPodIndex == idx) || touchscreenDetected)) &&  (
                                                            <div>
                                                                {getCellButtons(idx, props.section?.headerCells?.length ?? 0)}
                                                            </div>
                                                        )}
                                                        <div style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}>
                                                            {editMode &&
                                                                <React.Fragment>
                                                                    <TextField
                                                                        label={'Youtube Video Id or URL'}
                                                                        value={cell.sourceUrl}
                                                                        onChange={(e, v) => {handleHeaderCellValueChange({...cell, sourceUrl: v}, idx)}}
                                                                    />
                                                                </React.Fragment> }
                                                            <React.Fragment>
                                                                {(() => {
                                                                    const videoId = extractYouTubeVideoId(cell.sourceUrl ?? '');
                                                                    return videoId ? (
                                                                        <iframe
                                                                            height={cell.styleProps?.height ?? (!editMode ? '100%' : undefined)}
                                                                            width={cell.styleProps?.width ?? (!editMode ? '100%' : undefined)}
                                                                            src={`https://www.youtube.com/embed/${videoId}`}
                                                                            title="YouTube video player"
                                                                            frameBorder="0"
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                            allowFullScreen
                                                                        ></iframe>
                                                                    ) : null;
                                                                })()}
                                                            </React.Fragment>

                                                        </div>
                                                    </React.Fragment> : null}
                                            </React.Fragment>
                                        </div>
                                        {/* Grabber between cells, only in edit mode and not after last cell */}
                                        {editMode && idx < ((props.section?.headerCells?.length ?? 0) - 1) && (
                                            <div
                                                style={{ width: 8, cursor: 'col-resize', background: '#ccc', zIndex: 15, height: '100%' }}
                                                onMouseDown={(e) => handleHeaderGrabberMouseDown(idx, e)}
                                            />
                                        )}
                                    </React.Fragment>)
                                })}
                            </div>
                        </div>

                        {/* Product items row (mock) */}
                        <div className={(editMode ? 'card-container-edit' : '')}
                             onMouseEnter={() => setIsProductRowHovered(true)}
                             onMouseLeave={() => setIsProductRowHovered(false)}>
                            {editMode && (isProductRowHovered || touchscreenDetected) &&
                                <div>
                                    <CommandButton
                                        iconProps={{ iconName: 'EditStyle' }}
                                        menuProps={getMenuItems('Product')}
                                    />
                                </div>
                            }
                            {getMockItems()}
                        </div>
                        {/* Footer row */}
                        <div className={(editMode ? "header-row-container-edit" : "header-row-container")}
                             ref={footerRowRef}
                             onMouseEnter={() => setIsFooterRowHovered(true)}
                             onMouseLeave={() => setIsFooterRowHovered(false)}
                        >
                            {editMode && (isFooterRowHovered || touchscreenDetected || !props.section.footerCells || props.section.footerCells.length === 0) &&
                                <div>
                                    <CommandButton
                                        iconProps={{ iconName: 'EditStyle' }}
                                        menuProps={getMenuItems('Footer')}
                                    />
                                </div>}
                            <div style={{...props.section.footerStyleProps, display: 'flex', flexDirection: 'row', width: '100%'}}>
                                {(props.section.footerCells || []).map((cell, idx) => {

                                    const s = cell.styleProps || {};
                                    // If any cell has width defined, only cells without width fill remaining space
                                    const heightProps = {height: s.height, minHeight: s.minHeight, maxHeight: s.maxHeight};
                                    const widthProps = {width: s.width, minWidth: s.minWidth, maxWidth: s.maxWidth}
                                    let baseFlex = {};
                                    if (anyFooterCellWidthDefined) {
                                        if (!(s.width || s.minWidth || s.maxWidth)) {
                                            baseFlex = { flex: '1 1 0', ...heightProps };
                                        } else {
                                            baseFlex = { flex: '0 0 auto', ...widthProps, ...heightProps };
                                        }
                                    } else {
                                        baseFlex = { flex: '1 1 0', ...heightProps };
                                    }

                                    return(<React.Fragment key={idx}>
                                        <div
                                            onMouseEnter={() => {setIsFooterPodHovered(true); setHoveredFooterPodIndex(idx)}} onMouseLeave={() => {setIsFooterPodHovered(false); setHoveredFooterPodIndex(idx)}}
                                            className={(editMode ? "section-cell-edit" : "section-cell")}
                                            ref={el => footerCellAnchorRefs.current[idx] = el}
                                            style={{ position: 'relative', boxSizing: 'border-box',...baseFlex}}
                                        >
                                            <React.Fragment>
                                                {/* Render cell content by type */}
                                                {cell.type === 'image' ?
                                                    <React.Fragment>
                                                        {/* Add pod control buttons in edit mode */}
                                                        {(editMode && ((hoveredFooterPodIndex === idx && isFooterPodHovered) || touchscreenDetected)) && (
                                                            <div>
                                                                {getCellButtons(idx, props.section?.footerCells?.length ?? 0,true, true)}
                                                            </div>
                                                        )}
                                                        {editMode ?
                                                            <div style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}>
                                                                <ImageEditor value={cell.sourceUrl} onChange={(url) => {if(editMode){handleFooterCellValueChange({...cell,  sourceUrl: url}, idx)}}} width={'100%'} height={'100%'} />
                                                            </div> :
                                                            <Image src={cell.sourceUrl} style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}></Image>}
                                                    </React.Fragment> : null}
                                                {cell.type === 'text' ?
                                                    <React.Fragment>
                                                        {/* Add pod control buttons in edit mode */}
                                                        {(editMode && ((isFooterPodHovered && hoveredFooterPodIndex == idx) || touchscreenDetected)) &&  (
                                                            <div>
                                                                {getCellButtons(idx, props.section?.footerCells?.length ?? 0, true)}
                                                            </div>
                                                        )}

                                                        {editMode ?
                                                            <div>
                                                                <RichTextField previewStyle={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}  value={cell.text || ''} onChange={(url) => {if(editMode){handleFooterCellValueChange({...cell,  text: url}, idx)}}} readOnly={!editMode} />
                                                            </div>
                                                            :
                                                            <div>
                                                                <MarkdownView style={{width: '100%', height: '100%'}} className={'text-wysiwyg'} markdown={cell.text ?? ''} />
                                                            </div>}
                                                    </React.Fragment> : null}
                                                {cell.type === 'video' ?
                                                    <React.Fragment>
                                                        {/* Add pod control buttons in edit mode */}
                                                        {(editMode && ((isFooterPodHovered && hoveredFooterPodIndex == idx) || touchscreenDetected)) &&  (
                                                            <div>
                                                                {getCellButtons(idx, props.section?.footerCells?.length ?? 0, true)}
                                                            </div>
                                                        )}
                                                        <div style={{...cell.styleProps, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset'}}>
                                                            {editMode &&
                                                                <React.Fragment>
                                                                    <TextField
                                                                        label={'Youtube Video Id or URL'}
                                                                        value={cell.sourceUrl}
                                                                        onChange={(e, v) => {handleFooterCellValueChange({...cell, sourceUrl: v}, idx)}}
                                                                    />
                                                                </React.Fragment> }
                                                            <React.Fragment>
                                                                {(() => {
                                                                    const videoId = extractYouTubeVideoId(cell.sourceUrl ?? '');
                                                                    return videoId ? (
                                                                        <iframe
                                                                            src={`https://www.youtube.com/embed/${videoId}`}
                                                                            title="YouTube video player"
                                                                            frameBorder="0"
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                            allowFullScreen
                                                                        ></iframe>
                                                                    ) : null;
                                                                })()}
                                                            </React.Fragment>

                                                        </div>
                                                    </React.Fragment> : null}
                                            </React.Fragment>
                                        </div>
                                        {/* Grabber between cells, only in edit mode and not after last cell */}
                                        {editMode && idx < ((props.section?.footerCells?.length ?? 0) - 1) && (
                                            <div
                                                style={{ width: 8, cursor: 'col-resize', background: '#ccc', zIndex: 15, height: '100%' }}
                                                onMouseDown={(e) => handleFooterGrabberMouseDown(idx, e)}
                                            />
                                        )}
                                    </React.Fragment>)
                                })}
                            </div>
                        </div>
                    </div>
                </div> : <></>}
            </div>
        </div>
    );
}

export default StorefrontSection;
